import type { ReactNode } from 'react';

/**
 * **How an unconfirmed estate figure reads** (VALE-1479) — one definition, for
 * every surface that renders one.
 *
 * A money figure on an inventory item is either vouched for or it is not, and the
 * two must not look the same: a solicitor about to act on a number needs to know
 * whether anything stands behind it. The inventory list's current view has said so
 * since LF-4356 — an unconfirmed figure renders with a leading `~`, a confirmed
 * one carries a tick and its provenance — and the item drawer said nothing at
 * all, printing the same amount whether or not a source existed. Two surfaces
 * onto one number, disagreeing about how to say *estimate*, and the drawer is the
 * one the reader is about to act in.
 *
 * **The mark is the whole of the distinction** (VALE-1617). It was a mark AND a
 * muted ink until the review surfaces showed what the ink cost; see the note
 * where `ESTIMATE_INK` used to be.
 *
 * So the mark lives here rather than at either call site, beside
 * {@link ./confirmation-provenance.confirmationProvenanceTip} — which is the same
 * move for the other half of the same fact, and for the same reason: a lawyer
 * moving between the list and the drawer must not be told two different stories
 * about one figure.
 *
 * **The predicate is the part worth binding to.** *How* it is drawn is two tokens;
 * *when* it applies is the rule the surfaces have to agree on, and it is not
 * "`confirmed === false`": an absent figure is not an estimate of anything, and
 * each surface already has its own empty state for that (the list's dashed
 * `Add value` pill, the treatment's `Set <label>…` placeholder).
 */

/**
 * The leading mark on an estimated figure — `~$450.00`.
 *
 * A tilde rather than a word, because it sits inside a right-aligned money column
 * where a prefix costs one character and a suffix costs the alignment.
 *
 * An `EST` badge was tried here and withdrawn (VALE-1534) — the owner is
 * revisiting the treatment separately. Two things that pass are worth keeping
 * if it comes back: a badge has to be a SIBLING of the figure rather than a
 * prefix inside it, or it lands on the editable figure's `-mx-1.5` hover box
 * and sits 5px outside the column on exactly the rows that are editable; and
 * it has to pin to the column's left edge, or a column of them is as ragged as
 * the inline status chips this ticket moved into a column of their own.
 */
export const ESTIMATE_MARK = '~';

/**
 * **There is no estimate ink** (VALE-1617), and this note is here so the next
 * reader does not reintroduce one.
 *
 * An `ESTIMATE_INK` of `text-muted-foreground` sat here, and both surfaces drew
 * an unconfirmed figure in it. Nobody asked for the muting: it reads as
 * *disabled* rather than as *approximate*, and on the AI review surfaces it was
 * actively wrong — the ink lands on a span INSIDE the value cell, so it beat the
 * change ink the row had just been marked with, and an AI-supplied `DOD value`
 * rendered grey under a legend promising it in the new/updated colour.
 *
 * So an unconfirmed figure takes whatever ink its row would otherwise carry:
 * ordinary value ink at rest, the change ink on a review surface, the rejected
 * treatment when rejected. {@link ESTIMATE_MARK} alone carries the distinction,
 * which is what the mark is for.
 */

/**
 * **Whether a figure reads as an estimate**: recorded, but with nothing vouching
 * for it.
 *
 * Both clauses are load-bearing. A confirmed figure is not an estimate, and an
 * *absent* one is not an estimate either — `~` in front of nothing states an
 * approximation of a number that was never given. `0` is a recorded figure like
 * any other (LF-6093: a nil balance is a fact, not an absence), which is why the
 * test is against `null`/`undefined` rather than falsiness.
 */
export function isEstimatedFigure(
  amount: number | null | undefined,
  confirmed: boolean
): boolean {
  return amount != null && !confirmed;
}

/**
 * **An already-formatted figure, marked when it is an estimate.**
 *
 * For a surface whose figure is a run of text in a cell it does not otherwise
 * own — the inventory attribute drawer's value rows and the share recipes'
 * market-figure rows, which sit inside the attribute treatment's read-out. The
 * inventory list composes {@link ESTIMATE_MARK} itself instead, because there the
 * mark sits inside a right-aligned cell the list already owns.
 *
 * **It sets no colour of its own** (VALE-1617). That is what lets the row's ink
 * reach the figure: at rest the cell's ordinary value ink, on a review surface
 * the change ink the treatment marked the row with, and the rejected treatment
 * when the reviewer turns the value down. A wrapper that painted its own colour
 * here won all three, which is the bug this component used to have.
 */
export function EstimatedFigure({
  estimated,
  children
}: {
  estimated: boolean;
  children: ReactNode;
}) {
  /*
    Still a `span`, deliberately, even though it now carries no class: it is a
    flex item in every cell that renders one, and dropping to a fragment would
    make the figure and its suffix two anonymous items instead of one.
  */
  return (
    <span>
      {estimated ? ESTIMATE_MARK : ''}
      {children}
    </span>
  );
}
